import { Alert, AlertIcon, Box, Button, Flex, Heading, Icon, IconButton, Spinner, Tag, TagCloseButton, TagLabel, TagLeftIcon, Text, Textarea, VStack } from "@chakra-ui/react";
import { hasAnswerQuestion, splitAnswerQuestion } from "./answerChoices";
import { QuestionCard } from "./QuestionCard";
import { Tooltip } from "../components/Tooltip";
import { Fragment, useCallback, useContext, useEffect, useMemo, useRef, useState, type MouseEvent, type ReactNode } from "react";
import { AssistantPanelContext } from "./assistantPanelContext";
import { useTranslation } from "react-i18next";
import { FiArrowDown, FiArrowUp, FiCheck, FiFileText, FiPaperclip, FiPlus, FiShoppingCart, FiSquare, FiX } from "react-icons/fi";
import { TipTap } from "../input/TipTap/TipTap";
import { getTiptapJsonFromMarkdown } from "../input/TipTap/markdownUtils";
import { useAssistantContext } from "./AssistantProvider";
import { AssistantEmptyState } from "./AssistantEmptyState";
import { ConfirmationCard } from "./ConfirmationCard";
import type { PendingConfirmation } from "./ConfirmationCard";
import { withCardEdits } from "./confirmationCardEditing";
import { ConversationSwitcher } from "./ConversationSwitcher";
import { useConversationActions } from "./useConversationActions";
import { CONVERSATION_LIST_BREAKPOINT } from "./ConversationList";
import { DocumentSourceCards } from "./DocumentSourceCards";
import { useProcurementTerms } from "../terminology/useProcurementTerms";
import { EntityHighlightCards } from "./EntityHighlightCards";
import { ListLinks } from "./ListLinks";
import { ModelSwitcher } from "./ModelSwitcher";
import { entityIdFromHref, withoutUnroutableEntityLinks } from "./entityLinks";
import { withUnbreakableFigures } from "./unbreakableFigures";
import { nextRevealText } from "./textReveal";
import { useCurrentPageEntity } from "./useCurrentPageEntity";
import { isAnsweredElsewhere, subjectAwayFromPage, type AnsweredOn } from "./conversationSubject";
import { presentName, useEntityTitle, useUntitledName } from "./useEntityTitle";
import { casePath, useCaseNow } from "./useCaseNow";
import { LinkCard } from "./LinkCard";
import { CONVERSATION_FILE_ACCEPT, MAX_CONVERSATION_FILE_MB, type ConversationAttachment } from "./conversationFiles";
import { useConversationFiles, type ConversationFileFailure, type ConversationFileProblem } from "./useConversationFiles";
import { FailedUploadChip } from "../components/communication/FailedUploadChip";
import { READ_CONVERSATION_FILE_TOOL_NAME } from "./tools";
import { useFollowLatest } from "./useFollowLatest";
import { urls } from "../../urls";
import { useLocation } from "react-router-dom";
import { useGuardedNavigate } from "../navigation/useGuardedNavigate";

const COMPOSER_MAX_HEIGHT_PX = 160;

const REVEAL_TICK_MS = 40;

// Reveal a live-streamed text at a steady word cadence instead of the network's lumpy chunking —
// the word-for-word feel is an animation on top of the stream. Inactive (settled/restored) turns
// snap straight to the full text.
const useSmoothedText = (target: string, active: boolean): string => {
  const [displayed, setDisplayed] = useState(target);
  const targetRef = useRef(target);
  targetRef.current = target;
  const hasTarget = target !== "";

  useEffect(() => {
    if (!active) {
      setDisplayed(targetRef.current);
      return;
    }
    const handle = setInterval(() => {
      setDisplayed((current) => nextRevealText(current, targetRef.current));
    }, REVEAL_TICK_MS);
    return () => clearInterval(handle);
  }, [active, hasTarget]);

  return active ? displayed : target;
};

// The one answer renderer: streamed text re-parses through the same read-only rich-text path a
// restored turn uses, so live and restored answers are identical. While the turn is live the text
// runs through the typewriter reveal.
const SCROLL_COVER = "var(--chakra-colors-smBackground)";
const SCROLL_EDGE = "var(--chakra-colors-smScrollEdgeShadow)";

const ANSWER_STYLES = {
  ".tiptap p": { marginBottom: "0.75rem" },
  ".tiptap p:last-child": { marginBottom: 0 },
  ".tiptap table": {
    display: "block",
    width: "max-content",
    maxWidth: "full",
    overflowX: "auto",
    tableLayout: "auto",
    background: [
      `linear-gradient(to right, ${SCROLL_COVER} 30%, transparent) left center / 2.5rem 100% no-repeat local`,
      `linear-gradient(to left, ${SCROLL_COVER} 30%, transparent) right center / 2.5rem 100% no-repeat local`,
      `radial-gradient(farthest-side at 0 50%, ${SCROLL_EDGE}, transparent) left center / 1rem 100% no-repeat scroll`,
      `radial-gradient(farthest-side at 100% 50%, ${SCROLL_EDGE}, transparent) right center / 1rem 100% no-repeat scroll`,
    ].join(", "),
  },
  ".tiptap table + p": { marginTop: "0.75rem" },
  ".tiptap table + p:has(> .ProseMirror-trailingBreak:only-child)": { marginTop: 0 },
  ".tiptap th": { backgroundColor: "smBackgroundSecondary" },
  ".tiptap a": { color: "smPrimary", _hover: { color: "smPrimary", textDecoration: "underline" } },
  ".tiptap td, .tiptap th": {
    width: "auto",
    minWidth: "4rem",
    maxWidth: "16rem",
    overflowWrap: "normal",
    wordBreak: "normal",
    hyphens: "manual",
  },
};

export const AssistantAnswer = ({
  text,
  streaming,
  canOpenEntity,
  openEntity,
}: {
  text: string;
  streaming: boolean;
  canOpenEntity: (id: string) => boolean;
  openEntity: (id: string) => void;
}) => {
  const smoothed = useSmoothedText(text, streaming);
  const content = useMemo(
    () => withoutUnroutableEntityLinks(getTiptapJsonFromMarkdown(withUnbreakableFigures(smoothed)), canOpenEntity),
    [smoothed, canOpenEntity],
  );
  const openRowLink = (event: MouseEvent<HTMLDivElement>) => {
    const anchor = (event.target as HTMLElement).closest("a");
    const id = entityIdFromHref(anchor?.getAttribute("href"));
    if (id) {
      event.preventDefault();
      event.stopPropagation();
      if (canOpenEntity(id)) {
        openEntity(id);
      }
    }
  };
  return (
    <Box
      w="full"
      onClickCapture={openRowLink}
      sx={ANSWER_STYLES}
    >
      <TipTap content={content} editable={false} hasBorder={false} padding="0" maxHeight="full" />
    </Box>
  );
};

const ChoicesAnsweredElsewhere = ({ answeredOn }: { answeredOn: AnsweredOn }) => {
  const { t } = useTranslation();
  const navigate = useGuardedNavigate();
  const caseNow = useCaseNow(answeredOn);
  const untitledName = useUntitledName();
  const recordedTitle = useEntityTitle(answeredOn);
  if (caseNow.gone) {
    return (
      <Text fontSize="sm" color="smMuted">
        {answeredOn.kind === "project"
          ? t("These choices are about a project that no longer exists.")
          : t("These choices are about a procurement that no longer exists.")}
      </Text>
    );
  }
  const title = caseNow.title === undefined ? recordedTitle : presentName(caseNow.title) ?? untitledName(answeredOn.kind);
  const path = casePath(answeredOn);
  return (
    <VStack align="start" spacing="2">
      <Text fontSize="sm" color="smMuted">
        {t("These choices are about «{{name}}». Open it to use them.", { name: title })}
      </Text>
      {path && title && caseNow.found && (
        <LinkCard
          icon={answeredOn.kind === "v2Event" ? FiShoppingCart : undefined}
          label={title}
          sublabel={caseNow.referenceId}
          onOpen={() => navigate(path)}
        />
      )}
    </VStack>
  );
};

const AnswerWithChoices = ({
  text,
  streaming,
  isLatest,
  canChoose,
  answeredElsewhere,
  onChoose,
  onWriteYourself,
  canOpenEntity,
  openEntity,
  sentAnswer,
}: {
  text: string;
  streaming: boolean;
  isLatest: boolean;
  canChoose: boolean;
  answeredElsewhere: AnsweredOn | undefined;
  onChoose: (label: string) => void;
  onWriteYourself: () => void;
  canOpenEntity: (id: string) => boolean;
  openEntity: (id: string) => void;
  sentAnswer: string | undefined;
}) => {
  const { text: answer, question } = splitAnswerQuestion(text);
  const showsAnsweredElsewhere = question !== null && !streaming && isLatest && !!answeredElsewhere;
  const caseNow = useCaseNow(showsAnsweredElsewhere ? answeredElsewhere : undefined);
  const goneId = caseNow.gone ? answeredElsewhere?.id : undefined;
  const canOpenInAnswer = useCallback((id: string) => id !== goneId && canOpenEntity(id), [goneId, canOpenEntity]);
  return (
    <>
      {answer && (
        <AssistantAnswer text={answer} streaming={streaming} canOpenEntity={canOpenInAnswer} openEntity={openEntity} />
      )}
      {question && !streaming && (
        <QuestionCard
          key={text}
          question={question}
          isDisabled={!isLatest || !canChoose || !!answeredElsewhere}
          onAnswer={onChoose}
          onWriteYourself={onWriteYourself}
          sentAnswer={sentAnswer}
        />
      )}
      {showsAnsweredElsewhere && answeredElsewhere && <ChoicesAnsweredElsewhere answeredOn={answeredElsewhere} />}
    </>
  );
};

const AttachmentChips = ({ attachments }: { attachments: ConversationAttachment[] }) => (
  <Flex justify="flex-end" gap="1" flexWrap="wrap">
    {attachments.map((file) => (
      <Tag key={file.documentId} size="sm" variant="outline" colorScheme="gray" maxW="full">
        <TagLeftIcon as={FiFileText} />
        <TagLabel>{file.fileName}</TagLabel>
      </Tag>
    ))}
  </Flex>
);

const useFailureReasons = (): Record<ConversationFileFailure, string> => {
  const { t } = useTranslation();
  return {
    upload: t("Upload failed!"),
    refused: t("The security scan rejected this file. Remove it and upload a clean copy."),
    timeout: t("The file was not ready in time."),
  };
};

const useProblemText = (problem: ConversationFileProblem | null): string | null => {
  const { t } = useTranslation();
  switch (problem) {
    case "unsupportedType":
      return t("Only PDF and Word files (.pdf, .docx) can be attached.");
    case "tooLarge":
      return t("The file is larger than {{megabytes}} MB, the most the assistant can read.", {
        megabytes: MAX_CONVERSATION_FILE_MB,
      });
    default:
      return null;
  }
};

export interface AssistantChatHeading {
  title: string;
  subtitle: string;
  hidesSubtitleOnPhone?: boolean;
  action?: ReactNode;
}

export interface EmptyConversationActions {
  send: (text: string, startButtonLabel?: string) => void;
  pickFiles: (() => void) | null;
  writeInComposer: () => void;
  disabled: boolean;
}

export const AssistantChat = ({
  heading,
  emptyState,
  aboveConversation,
  conversationsListedBeside = false,
  inputBlockedReason,
}: {
  heading?: AssistantChatHeading;
  emptyState?: (actions: EmptyConversationActions) => ReactNode;
  aboveConversation?: (actions: EmptyConversationActions) => ReactNode;
  conversationsListedBeside?: boolean;
  inputBlockedReason?: string;
}) => {
  const { t } = useTranslation();
  const terms = useProcurementTerms();
  const handoff = useContext(AssistantPanelContext)?.handoff;
  const { returnToPreviousConversation, startNewConversation } = useConversationActions();
  const {
    previousConversation,
    startsNewWhenAnswered,
    activeConversationId,
    isRestoring,
    turns,
    isRunning,
    activeStep,
    pendingConfirmation,
    confirm,
    cancel,
    refine,
    lookupEntity,
    navigateToEntity,
    canOpenEntity,
    send,
    stop,
    stopping,
    retry,
    draft,
    setDraft,
    toolsLoading,
    toolsFailed,
    assistantNotIncluded,
    aiNotAllowed,
    retryTools,
    enabledTools,
    withheldByAiSetting,
    subject,
  } = useAssistantContext();
  const composerHeld = toolsLoading || assistantNotIncluded || aiNotAllowed;
  const inputBlocked = !!inputBlockedReason;
  const conversationFiles = useConversationFiles();
  const fileProblem = useProblemText(conversationFiles.problem);
  const failureReason = useFailureReasons();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const canReadFiles = enabledTools.has(READ_CONVERSATION_FILE_TOOL_NAME);
  const filesWithheld = withheldByAiSetting.has(READ_CONVERSATION_FILE_TOOL_NAME);
  const attachLabel = filesWithheld
    ? t("Your organization's AI setting does not allow attachments: files are read on AWS Bedrock in the EU or by OpenAI, and neither is allowed")
    : t("Attach a PDF or Word file");
  const canSend =
    !conversationFiles.uploading && (draft.trim() !== "" || (conversationFiles.hasReady && !pendingConfirmation));
  const pageEntity = useCurrentPageEntity();
  const viewedEntity = subjectAwayFromPage(subject, pageEntity) ?? pageEntity;
  const transcriptRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);

  const follow = useFollowLatest(transcriptRef);
  const { contentChanged, followAgain, startOver } = follow;
  const onAssistantPage = useLocation().pathname === urls.app.assistant;
  const restoring = isRestoring && turns.length === 0;
  const startsCentred = onAssistantPage && turns.length === 0 && !emptyState && !restoring;
  const shownConversation = useRef(activeConversationId);

  useEffect(() => {
    const switched = shownConversation.current !== activeConversationId;
    shownConversation.current = activeConversationId;
    if (turns.length === 0) startOver();
    else if (switched) followAgain();
    else contentChanged();
  }, [turns, isRunning, activeStep, pendingConfirmation, activeConversationId, contentChanged, followAgain, startOver]);

  useEffect(() => {
    const composer = composerRef.current;
    if (!composer) {
      return;
    }
    composer.style.height = "auto";
    composer.style.height = `${Math.min(composer.scrollHeight, COMPOSER_MAX_HEIGHT_PX)}px`;
  }, [draft]);

  const cardEditsRef = useRef<{ pending: PendingConfirmation; line: string | null } | null>(null);

  const submit = () => {
    const text = draft.trim();
    if (!canSend || inputBlocked) {
      return;
    }
    // While a confirmation card is up, a new message refines the proposal instead of being blocked.
    if (pendingConfirmation) {
      setDraft("");
      const cardEdits = cardEditsRef.current?.pending === pendingConfirmation ? cardEditsRef.current.line : null;
      refine(withCardEdits(text, cardEdits));
      return;
    }
    if (isRunning || composerHeld) {
      return;
    }
    setDraft("");
    follow.followAgain();
    void send(text, undefined, conversationFiles.takeReady());
  };

  const sendPrompt = (text: string, startButtonLabel?: string) => {
    if (isRunning || composerHeld) {
      return;
    }
    setDraft("");
    follow.followAgain();
    void send(text, startButtonLabel);
  };

  const conversationActions: EmptyConversationActions = {
    send: sendPrompt,
    pickFiles: canReadFiles && !composerHeld ? () => fileInputRef.current?.click() : null,
    writeInComposer: () => composerRef.current?.focus(),
    disabled: composerHeld || isRunning,
  };

  const bottom = (
    <>
      {toolsFailed && (
        <Alert status="error" rounded="md" mt="3" fontSize="sm" flexShrink={0}>
          <AlertIcon />
          <Box flex="1">{t("The assistant couldn't load your organisation's settings.")}</Box>
          <Button size="sm" variant="outline" onClick={() => void retryTools()}>
            {t("Try again")}
          </Button>
        </Alert>
      )}
      {assistantNotIncluded && (
        <Alert status="info" rounded="md" mt="3" fontSize="sm" flexShrink={0}>
          <AlertIcon />
          <Box flex="1">{t("Your organization does not have access to this feature.")}</Box>
        </Alert>
      )}
      {aiNotAllowed && (
        <Alert status="info" rounded="md" mt="3" fontSize="sm" flexShrink={0}>
          <AlertIcon />
          <Box flex="1">{t("zz_AgentApiErrorCode_AiNotAllowedForOrganization")}</Box>
        </Alert>
      )}
      {toolsLoading && (
        <Flex align="center" gap="2" mt="3" color="smTextDisabled" fontSize="sm" flexShrink={0}>
          <Spinner size="xs" />
          <Text>{t("Getting the assistant ready…")}</Text>
        </Flex>
      )}

      {(conversationFiles.files.length > 0 || fileProblem) && (
        <Flex direction="column" gap="2" mt="3" flexShrink={0}>
          {conversationFiles.files.length > 0 && (
            <Flex gap="1" flexWrap="wrap">
              {conversationFiles.files.map((file) =>
                file.state === "failed" ? (
                  <FailedUploadChip
                    key={file.id}
                    fileName={file.fileName}
                    onRetry={() => void conversationFiles.retry(file.id)}
                    onRemove={() => conversationFiles.remove(file.id)}
                  />
                ) : (
                  <Tag key={file.id} size="md" variant="outline" colorScheme="gray" maxW="full">
                    {file.state === "uploading" ? <Spinner size="xs" mr="2" /> : <TagLeftIcon as={FiFileText} />}
                    <TagLabel>{file.fileName}</TagLabel>
                    <TagCloseButton
                      aria-label={t("Remove {{fileName}}", { fileName: file.fileName }) ?? ""}
                      onClick={() => conversationFiles.remove(file.id)}
                    />
                  </Tag>
                ),
              )}
            </Flex>
          )}
          {conversationFiles.files.flatMap((file) =>
            file.state === "failed" && file.failure ? (
              <Text key={`failure-${file.id}`} fontSize="sm" color="smError" role="alert">
                {file.fileName}: {failureReason[file.failure]}
              </Text>
            ) : [],
          )}
          {fileProblem && (
            <Text fontSize="sm" color="smError" role="alert">
              {fileProblem}
            </Text>
          )}
        </Flex>
      )}

      {inputBlocked && (
        <Text fontSize="sm" color="smMuted" mt="3" flexShrink={0}>
          {inputBlockedReason}
        </Text>
      )}
      <Flex
        align="flex-end"
        gap="2"
        mt="3"
        flexShrink={0}
        bg="smBackground"
        borderWidth="1px"
        borderColor="smBorder"
        borderRadius="24px"
        px="3"
        py="2"
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={CONVERSATION_FILE_ACCEPT}
          hidden
          disabled={composerHeld || !canReadFiles}
          data-testid="assistant-file-input"
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = "";
            if (file) void conversationFiles.attach(file);
          }}
        />
        {(canReadFiles || filesWithheld) && (
          <Tooltip label={attachLabel} hasArrow>
            <IconButton
              aria-label={attachLabel ?? ""}
              icon={<FiPaperclip />}
              onClick={() => fileInputRef.current?.click()}
              isRound
              variant="ghost"
              color="smTextDisabled"
              flexShrink={0}
              isDisabled={composerHeld || !canReadFiles}
            />
          </Tooltip>
        )}
        <Textarea
          ref={composerRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          placeholder={
            (pendingConfirmation
              ? t("Type an adjustment …")
              : handoff && turns.length === 0
                ? t("Ask about {{source}} …", { source: handoff.source })
                : t("Write a message …")) ?? ""
          }
          isDisabled={(isRunning && !pendingConfirmation) || composerHeld || inputBlocked}
          variant="unstyled"
          rows={1}
          resize="none"
          minH="40px"
          maxH={`${COMPOSER_MAX_HEIGHT_PX}px`}
          overflowY="auto"
          px="2"
          py="2"
          flex="1"
        />
        {isRunning && !pendingConfirmation ? (
          <IconButton
            aria-label={t("Stop") ?? "Stop"}
            title={t("Stop") ?? ""}
            icon={<FiSquare />}
            onClick={stop}
            isLoading={stopping}
            isDisabled={stopping}
            isRound
            bg="smPrimary"
            color="white"
            _hover={{ bg: "smPrimarySolid" }}
            flexShrink={0}
          />
        ) : (
          <IconButton
            aria-label={t("Send") ?? "Send"}
            icon={<FiArrowUp />}
            onClick={submit}
            isRound
            bg="smPrimary"
            color="white"
            _hover={{ bg: "smPrimarySolid" }}
            flexShrink={0}
            isDisabled={composerHeld || !canSend || inputBlocked}
          />
        )}
      </Flex>
    </>
  );

  return (
    <Flex direction="column" h="full" w="full" maxW="4xl" mx="auto">
      <Flex align="center" justify="space-between" gap="3" mb="1" flexWrap="wrap">
        <Heading size="md" color="smText">
          {heading?.title ?? t("The assistant")}
        </Heading>
        <Flex align="center" gap="1" minW="0" maxW="full" flexWrap="wrap">
          <Box flexShrink={0}>
            <ModelSwitcher />
          </Box>
          {!conversationsListedBeside && <ConversationSwitcher />}
        </Flex>
      </Flex>
      {conversationsListedBeside && (
        <Flex
          display={{ base: "flex", [CONVERSATION_LIST_BREAKPOINT]: "none" }}
          align="center"
          gap="2"
          mb="2"
          data-testid="assistant-phone-conversation-controls"
        >
          <Button
            leftIcon={<FiPlus />}
            size="sm"
            h="44px"
            colorScheme="teal"
            variant="outline"
            onClick={startNewConversation}
            isDisabled={isRunning}
          >
            {t("New conversation")}
          </Button>
          <ConversationSwitcher labelledAsConversations />
        </Flex>
      )}
      <Flex
        align="baseline"
        justify="space-between"
        gap="3"
        mb="4"
        flexWrap="wrap"
        display={conversationsListedBeside && !heading?.action ? { base: "none", [CONVERSATION_LIST_BREAKPOINT]: "flex" } : "flex"}
        data-testid="assistant-intro"
      >
        <Text fontSize="sm" color="smMuted" display={heading?.hidesSubtitleOnPhone ? { base: "none", md: "block" } : undefined}>
          {heading?.subtitle ?? terms.assistantAskPlaceholder}
        </Text>
        {heading?.action}
      </Flex>

      {startsCentred ? (
        <Flex flex="1" minH="0" direction="column" justify="center" overflowY="auto" py="4">
          <AssistantEmptyState
            onPrompt={sendPrompt}
            pageKind={pageEntity?.kind ?? null}
            promptsDisabled={composerHeld}
            previousConversationTitle={previousConversation?.title}
            onReturnToPrevious={returnToPreviousConversation}
            composer={bottom}
          />
        </Flex>
      ) : (
        <Box position="relative" flex="1" minH="0" display="flex" flexDirection="column">
      {aboveConversation?.(conversationActions)}
      <Box
        ref={transcriptRef}
        onScroll={follow.onScroll}
        data-testid="assistant-transcript"
        flex="1"
        minH="0"
        overflowY="auto"
        {...(onAssistantPage
          ? { py: "2" }
          : { bg: "smBackground", borderWidth: "1px", borderColor: "smBorder", borderRadius: "lg", p: "4" })}
      >
        <VStack align="stretch" spacing="4">
          {startsNewWhenAnswered && (
            <Alert status="info" rounded="md" fontSize="sm" data-testid="starts-new-when-answered">
              <AlertIcon />
              {t("A new conversation starts as soon as this answer is done.")}
            </Alert>
          )}
          {turns.length === 0 && emptyState?.(conversationActions)}
          {restoring && (
            <Flex justify="center" py="10">
              <Spinner color="smPrimary" aria-label={t("Opening the conversation …") ?? undefined} />
            </Flex>
          )}
          {turns.length === 0 && !emptyState && !restoring && (
            <AssistantEmptyState
              onPrompt={sendPrompt}
              pageKind={pageEntity?.kind ?? null}
              promptsDisabled={composerHeld}
              previousConversationTitle={previousConversation?.title}
              onReturnToPrevious={returnToPreviousConversation}
            />
          )}

          {turns.map((turn, i) => {
            const streaming = isRunning && i === turns.length - 1;
            const isLatest = i === turns.length - 1;
            const answeredElsewhere = isAnsweredElsewhere(turn.answeredOn, viewedEntity) ? turn.answeredOn : undefined;
            const linksElsewhere =
              !!answeredElsewhere &&
              !!casePath(answeredElsewhere) &&
              isLatest &&
              !streaming &&
              hasAnswerQuestion(turn.answerText);
            const entities = linksElsewhere
              ? turn.entities?.filter((entity) => entity.id !== answeredElsewhere.id)
              : turn.entities;
            return (
              <Fragment key={i}>
                {turn.user && (
                  <Flex justify="flex-end">
                    <Box
                      maxW="75%"
                      px="4"
                      py="2"
                      borderRadius="16px 16px 4px 16px"
                      bg="smPrimary"
                      color="white"
                      fontSize="sm"
                      whiteSpace="pre-wrap"
                      wordBreak="break-word"
                    >
                      {turn.user}
                    </Box>
                  </Flex>
                )}
                {turn.attachments && turn.attachments.length > 0 && <AttachmentChips attachments={turn.attachments} />}

                {turn.steps.map((step, j) => (
                  <Flex key={j} align="center" gap="2" color="smTextDisabled" fontSize="xs">
                    <Icon as={step.ok ? FiCheck : FiX} color={step.ok ? "smPrimary" : "red.400"} />
                    <Text>{step.label}</Text>
                  </Flex>
                ))}

                {turn.note && !turn.failed && (
                  <Text fontSize="sm" color="smTextDisabled">
                    {turn.note}
                  </Text>
                )}

                {turn.answerText && (
                  <AnswerWithChoices
                    text={turn.answerText}
                    streaming={streaming}
                    isLatest={isLatest}
                    canChoose={!isRunning && !composerHeld && !inputBlocked}
                    answeredElsewhere={answeredElsewhere}
                    onChoose={sendPrompt}
                    onWriteYourself={() => composerRef.current?.focus()}
                    canOpenEntity={canOpenEntity}
                    openEntity={navigateToEntity}
                    sentAnswer={turns[i + 1]?.user}
                  />
                )}

                {turn.lists && turn.lists.length > 0 && !(isRunning && i === turns.length - 1) && (
                  <ListLinks lists={turn.lists} />
                )}

                {entities && entities.length > 0 && (
                  <EntityHighlightCards entities={entities} lookupEntity={lookupEntity} onOpen={navigateToEntity} />
                )}

                {turn.sources && turn.sources.length > 0 && <DocumentSourceCards sources={turn.sources} />}

                {turn.note && turn.failed && (
                  <Alert status="error" rounded="md" fontSize="sm">
                    <AlertIcon />
                    <Box flex="1">
                      {turn.note}
                      {turn.changedSomething && (
                        <Text mt="1">{t("Part of it was already carried out, so ask again to continue from here.")}</Text>
                      )}
                    </Box>
                    {turn.retryable && i === turns.length - 1 && (
                      <Button size="sm" variant="outline" isDisabled={isRunning} onClick={retry}>
                        {t("Try again")}
                      </Button>
                    )}
                  </Alert>
                )}
              </Fragment>
            );
          })}

          {pendingConfirmation && (
            <ConfirmationCard
              pending={pendingConfirmation}
              lookupEntity={lookupEntity}
              onConfirm={confirm}
              onCancel={cancel}
              isBusy={false}
              onEditsChange={(line) => {
                cardEditsRef.current = { pending: pendingConfirmation, line };
              }}
            />
          )}

          {isRunning && activeStep && (
            <Flex align="center" gap="2" color="smTextDisabled" fontSize="sm">
              <Spinner size="xs" />
              <Text>{`${activeStep}…`}</Text>
            </Flex>
          )}

        </VStack>
      </Box>
          {follow.hasUnseenLatest && turns.length > 0 && (
            <Flex
              data-testid="new-answer-backdrop"
              position="absolute"
              bottom="0"
              left="0"
              right="0"
              justify="center"
              pt="8"
              pb="2"
              bgGradient="linear(to bottom, transparent, smBackground 60%)"
              pointerEvents="none"
            >
              <Button
                minH="44px"
                px="5"
                rounded="full"
                shadow="md"
                bg="smBackground"
                borderWidth="1px"
                borderColor="smBorder"
                pointerEvents="auto"
                leftIcon={<Icon as={FiArrowDown} />}
                onClick={follow.followAgain}
              >
                {t("New answer")}
              </Button>
            </Flex>
          )}
        </Box>
      )}

      {startsCentred ? null : bottom}
    </Flex>
  );
};
